/**
 * Engagement schemas.
 *
 * An engagement is a business-setup job for a client (OPS 3.7 step 2): the
 * interest field is dead on the web (new rows default BUSINESS_SETUP;
 * input/output kept for the installed mobile app), and pipeline stages move
 * only via the proposal status chain (OPS 3.5 B): signature flips the
 * engagement to ENGAGED. Client activation is lane-split — a RECURRING
 * (compliance-retainer) signature flips the client to ACTIVE; a ONE_OFF
 * (setup) signature does NOT (activation waits for the post-checklist fork).
 */

import { z } from 'zod';
import {
  idSchema, iso8601Schema, moneySchema, currencySchema, pageInput, sortOrderSchema, booleanQuerySchema,
} from './common';
import { JURISDICTION_CODES } from '../reference/jurisdictions';
import { USER_ROLES } from '../reference/roles';

export const engagementTypeSchema = z.enum([
  'ONE_TIME',
  'RETAINER',
  'ANNUAL',
]);

export const engagementPipelineSchema = z.enum([
  'CONTACTED',
  'PROPOSAL_SENT',
  'NEGOTIATION',
  'ENGAGED',
  'DEFERRED',
  'NOT_INTERESTED',
]);

export const engagementInterestSchema = z.enum([
  'BUSINESS_SETUP',
  'ACCOUNTING',
  'TAXATION',
  'AUDIT',
  'ADVISORY',
  'FP_AND_A',
  'PAYROLL',
  'CORPORATE',
]);
export type EngagementInterest = z.infer<typeof engagementInterestSchema>;

export const engagementOutputSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  clientName: z.string().max(200).nullable(),
  clientCompany: z.string().max(200).nullable(),
  clientEmail: z.string().email().max(254).nullable(),
  title: z.string().min(1).max(200).nullable(),
  type: engagementTypeSchema.nullable(),
  interest: engagementInterestSchema.nullable(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable(),
  pipeline: engagementPipelineSchema,
  feeAmount: z.number().int().nullable(),
  feeCurrency: currencySchema.nullable(),
  // OPS 3.5 C: kick-off is dead — the columns still exist in the DB and are
  // still emitted here ONLY because the installed mobile app renders
  // e.kickoffTask (no runtime parse, but `.replace` on a missing value would
  // crash it). Nothing writes them anymore; drop on the mobile rebuild.
  kickoffTask: z.enum(['NOT_STARTED', 'STARTED', 'COMPLETED']),
  kickoffCompletedAt: iso8601Schema.nullable(),
  retainerWonAt: iso8601Schema.nullable(),
  managerId: idSchema.nullable(),
  agentId: idSchema.nullable(),
  startDate: iso8601Schema.nullable(),
  endDate: iso8601Schema.nullable(),
  renewalDate: iso8601Schema.nullable(),
  remarks: z.string().max(2000).nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type EngagementOutput = z.infer<typeof engagementOutputSchema>;

export const createEngagementInputSchema = z.object({
  clientId: idSchema,
  title: z.string().min(1).max(200).nullable().optional(),
  type: engagementTypeSchema.nullable().optional(),
  interest: engagementInterestSchema.nullable().optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable().optional(),
  feeAmount: moneySchema.nullable().optional(),
  managerId: idSchema.nullable().optional(),
  agentId: idSchema.nullable().optional(),
  startDate: iso8601Schema.nullable().optional(),
  endDate: iso8601Schema.nullable().optional(),
  renewalDate: iso8601Schema.nullable().optional(),
  pipeline: engagementPipelineSchema.optional(),
  remarks: z.string().max(2000).nullable().optional(),
});
export type CreateEngagementInput = z.infer<typeof createEngagementInputSchema>;

export const updateEngagementInputSchema = createEngagementInputSchema.partial().extend({
  pipeline: engagementPipelineSchema.optional(),
});
export type UpdateEngagementInput = z.infer<typeof updateEngagementInputSchema>;

export const listEngagementsInputSchema = pageInput.extend({
  clientId: idSchema.optional(),
  pipeline: engagementPipelineSchema.optional(),
  type: engagementTypeSchema.optional(),
  managerId: idSchema.optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'client', 'type', 'jurisdiction', 'pipeline', 'startDate', 'endDate']).default('updatedAt'),
  sortOrder: sortOrderSchema,
  /** When true, filter to engagements assigned to the current user. */
  assignedToMe: booleanQuerySchema.optional(),
  /** When true, includes non-active statuses. Default false. */
  all: booleanQuerySchema.optional(),
});
export type ListEngagementsInput = z.infer<typeof listEngagementsInputSchema>;
